import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { formatFormPhone } from 'assets/utils/forms/formatFormPhone';
import { getClaimCreatedByApp } from 'assets/utils/other/Utils.Other.ClaimEdit';
import { isEmail, isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { ApiHeaderApplication, HttpStatus } from 'config/Api.Config';
import { set } from 'lodash';
import claimUpdateClaimPayeeInfoApi from 'models/claims/claim/updateClaimPayeeInfo/Api.Claim.UpdateClaimPayeeInfo';
import Payee from 'models/claims/payee/Model.Payee';
import PayeeType from 'models/claims/payeeType/Model.PayeeType';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { ClaimCreationType } from 'models/enums/ClaimCreation';

type Props = {
  data?: Model.IClaim;
  onCancel(): void;
  onSave(): void;
};

const CreatedFromApplication = {
  PUBLIC: ApiHeaderApplication.public,
  ASC: ApiHeaderApplication.claims,
} as const;
const CreatedAs = {
  DEALERSHIP: PayeeType.DEALER_ID,
  REPAIR_FACILITY: PayeeType.REPAIR_FACILITY_ID,
  CUSTOMER: PayeeType.CLIENT_ID,
  OTHER: PayeeType.OTHER_ID,
} as const;

export default function ClaimEditContactInfoForm(props: Props) {
  const { lang, translate } = getLocales();
  const { dealerId } = useAuthStore();
  const dealerListApi = useApi({
    action: Dealer.list,
  });
  const repairFacilityListApi = useApi({
    action: Payee.list,
    body: { payeeTypeId: [PayeeType.REPAIR_FACILITY_ID] },
  });

  return (
    <ModalForm<Api.Core.Claim.UpdateClaimPayeeInfo.IRequest>
      subcomponents={ThemeModalFormDefault}
      default={{
        claimId: props.data?.id,
        claimPayee: {
          ...props?.data?.createdByPayee,
          name: props.data?.createdByPayee?.name,
          email: props.data?.createdByPayee?.email,
          phoneNumber: props.data?.createdByPayee?.phoneNumber,
        },
        createdFromApplicationId: props.data?.createdFromApplicationId,
        dealerId: props.data?.dealerId ?? dealerId,
      }}
      dependencyArray={[props.data]}
      title={lang.edit}
      onCancel={props.onCancel}
      onSubmit={async (data) => {
        const res = await claimUpdateClaimPayeeInfoApi(data);
        if (res.status === HttpStatus.ok) {
          addLog({ success: lang.saveSuccess });
          props.onSave();
        } else {
          addLog({ error: res.message });
        }
        return props.onCancel();
      }}
      validation={(data, errors) => {
        if (!data?.claimPayee?.name) set(errors, 'claimPayee.name', lang.isRequired);
        if (!data?.claimPayee?.payeeTypeId) set(errors, 'claimPayee.payeeTypeId', lang.isRequired);
        if (!data?.claimPayee?.email) set(errors, 'claimPayee.email', lang.isRequired);
        if (data?.claimPayee?.email && !isEmail(data?.claimPayee?.email))
          set(errors, 'claimPayee.email', lang.invalidEmailFormatRequires);
        if (data?.claimPayee?.phoneNumber && !isValidPhoneNumber(data?.claimPayee?.phoneNumber))
          set(errors, 'claimPayee.phoneNumber', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
        if (!data?.claimPayee?.email) set(errors, 'claimPayee.email', lang.isRequired);
        if (!data?.dealerId && PayeeType.DEALER_ID == data.claimPayee?.payeeTypeId)
          set(errors, 'dealerId', lang.isRequired);
      }}
      beforeUpdate={(data) => {
        if (data.claimPayee.payeeTypeId !== PayeeType.DEALER_ID) data.dealerId = null;

        return data;
      }}
      render={({ form }) => (
        <FormWrapper>
          <SelectInput
            name="payeeTypeId"
            label={lang.createdBy}
            data={Object.entries(CreatedAs).map(([key, value]) => ({
              id: value,
              title: translate(key),
            }))}
            value={form.data?.claimPayee?.payeeTypeId}
            onChange={({ payeeTypeId }) =>
              form.merge({
                claimPayee: { payeeTypeId },
              })
            }
            error={form.errors?.claimPayee?.payeeTypeId}
          />
          {PayeeType.REPAIR_FACILITY_ID === form.data?.claimPayee?.payeeTypeId &&
            getClaimCreatedByApp(form.data as Model.IClaim) === ClaimCreationType.PUBLIC_LINK && (
              <SelectInput
                name="repairFacilityId"
                label={lang.repairFacility}
                data={repairFacilityListApi.payload?.data?.map((it) => it.displayInfo)}
                value={form.data?.repairFacilityId}
                error={form.errors?.repairFacilityId}
                onChange={({ repairFacilityId }) => {
                  const payee = repairFacilityListApi.payload?.data?.find((it) => it.id === repairFacilityId);
                  form.merge({
                    repairFacilityId,
                    claimPayee: {
                      name: payee?.name,
                      email: payee?.email,
                      payeeTypeId: payee?.payeeTypeId,
                      phoneNumber: payee?.phoneNumber,
                    },
                  });
                }}
              />
            )}
          {PayeeType.DEALER_ID === form.data?.claimPayee?.payeeTypeId && (
            <SelectInput
              name="dealerId"
              label={lang.dealership}
              data={dealerListApi.payload?.data?.map((it) => it.displayInfo)}
              value={form.data?.dealerId}
              error={form.errors?.dealerId}
              onChange={form.update}
            />
          )}
          <SelectInput
            name="createdFromApplicationId"
            label={lang.createdBy}
            data={Object.entries(CreatedFromApplication).map(([key, value]) => ({
              id: value,
              title: translate(key),
            }))}
            value={form.data?.createdFromApplicationId}
            onChange={form.update}
            error={form.errors?.createdFromApplicationId}
          />
          <TextInput
            name="name"
            label={`${lang.contact} ${lang.name}`}
            value={form.data?.claimPayee?.name}
            onChange={({ name }) =>
              form.merge({
                claimPayee: { name },
              })
            }
            error={form.errors?.claimPayee?.name}
          />
          <TextInput
            name="email"
            label={`${lang.contact} ${lang.email}`}
            value={form.data?.claimPayee?.email}
            onChange={({ email }) =>
              form.merge({
                claimPayee: { email },
              })
            }
            error={form.errors?.claimPayee?.email}
          />
          <TextInput
            name="phone"
            label={`${lang.contact} ${lang.phoneNumber}`}
            value={form.data?.claimPayee?.phoneNumber}
            onChange={({ phone }) =>
              form.merge({
                claimPayee: { phoneNumber: formatFormPhone(form.data?.claimPayee?.phoneNumber, phone) },
              })
            }
            error={form.errors?.claimPayee?.phoneNumber}
          />
        </FormWrapper>
      )}
    />
  );
}
